'use client';

import React, { useEffect, useState } from 'react';
import { Block, BLOCKS, Document, TopLevelBlock, TOP_LEVEL_BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

// Safe guard to check if the block has content
function hasContent(block: Block): block is Block & { content: Block[] } {
  return Array.isArray((block as Block).content);
}

// Convert block to a React component (mimics documentToReactComponents for blocks)
export function blockToReactComponents(block: Block) {
  const isTopLevel = TOP_LEVEL_BLOCKS.includes(block.nodeType as (typeof TOP_LEVEL_BLOCKS)[number]);

  // Special handling for table cells to avoid nested paragraphs
  const isTableCell = block.nodeType === 'table-cell' || block.nodeType === 'table-header-cell';

  if (isTableCell && hasContent(block)) {
    // For table cells, render the content directly without wrapping
    const doc: Document = {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: block.content as TopLevelBlock[],
    };
    return documentToReactComponents(doc);
  }

  const doc: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: isTopLevel
      ? [block as TopLevelBlock]
      : [
          {
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: hasContent(block) ? block.content : [],
          } as TopLevelBlock,
        ],
  };
  return documentToReactComponents(doc);
}

// Check if the window is mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

// Split the table data by columns
function splitTableByColumns(tableNode: Block) {
  const rows = tableNode.content as Block[]; // Each row is a BLOCKS.TABLE_ROW
  const columns = (rows[0]?.content as Block[])?.length || 0;

  const columnData = Array.from({ length: columns }, (_, colIdx) =>
    rows.map((row: Block) => (row.content as Block[])[colIdx]),
  );

  return columnData;
}

// Render columns as separate tables on mobile
const TableRenderer = ({ node, children }: { node: Block; children: React.ReactNode }) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    const columns = splitTableByColumns(node);
    return (
      <div className="flex flex-col gap-4">
        {columns.map((col, i) => (
          <table key={i} className="w-full text-left">
            <tbody>
              {col.map((cell, j) => (
                <tr key={j} className="border-b border-pebble">
                  {cell.nodeType === 'table-header-cell' ? (
                    <th className="font-sans font-semibold py-6">{blockToReactComponents(cell)}</th>
                  ) : (
                    <td className="py-6 text-(--table-text-color)">{blockToReactComponents(cell)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    );
  }

  // Default desktop rendering
  return <table className="w-full text-left">{children}</table>;
};

const ResponsiveTable = ({ node, children }: { node: Block; children: React.ReactNode }) => {
  return <TableRenderer node={node}>{children}</TableRenderer>;
};

export default ResponsiveTable;
